import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { RetentionDashboard } from "../types";
import { useToast } from "../context/ToastContext";
import { useDrawer } from "../context/DrawerContext";
import { formatCurrency, formatDateTime } from "../lib/format";

function formatPct(n: number | undefined | null) {
  if (n === undefined || n === null) return "-";
  return `${n.toFixed(1)}%`;
}

const POSTCARE_LABELS: Record<string, string> = {
  CHECKIN: "Acompañamiento",
  FOLLOWUP_24H: "Seguimiento 24h",
  FOLLOWUP_72H: "Seguimiento 72h",
  SATISFACTION: "Encuesta de satisfacción",
  PROFESSIONAL_CONTROL: "Control profesional",
};

export default function Postventa() {
  const { show } = useToast();
  const { open } = useDrawer();
  const [dashboard, setDashboard] = useState<RetentionDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    api
      .getRetentionDashboard()
      .then(setDashboard)
      .catch((e) => {
        if (e instanceof ApiClientError) setError(e.message);
        else setError("No se pudo cargar el dashboard de postventa.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboard-page">
      <h1>Postventa y retención</h1>
      <p className="meta">Satisfacción, reputación, incidencias y recompra — reconciliado directamente de la actividad postventa.</p>

      {loading && <div className="loading-strip">Cargando métricas...</div>}
      {error && <p className="form-error">{error}</p>}

      {dashboard && (
        <div className="kpi-cards">
          <div className="kpi-card">
            <div className="muted">Pacientes recurrentes</div>
            <div className="big">{formatPct(dashboard.summary.pacientes_recurrentes_pct)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">LTV BCM promedio</div>
            <div className="big">{formatCurrency(dashboard.summary.ltv_bcm_promedio)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">Reseñas obtenidas</div>
            <div className="big">{formatPct(dashboard.summary.tasa_resenas_pct)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">Incidencias abiertas</div>
            <div className="big">{dashboard.summary.incidencias_abiertas}</div>
          </div>
        </div>
      )}

      {dashboard?.note && (
        <div className="dashboard-section">
          <p className="meta">{dashboard.note}</p>
        </div>
      )}

      {dashboard?.postventa_activa && (
        <div className="section-card">
          <h2>Postventa activa</h2>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Evento</th>
                <th>Programado</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.postventa_activa.map((pe) => (
                <tr key={pe.id}>
                  <td>
                    <button className="link-btn" onClick={() => pe.contact_id && open(pe.contact_id)}>
                      Paciente #{pe.contact_id}
                    </button>
                  </td>
                  <td>{POSTCARE_LABELS[pe.event_type] || pe.event_type}</td>
                  <td>{formatDateTime(pe.scheduled_at)}</td>
                  <td>
                    <span className={`pill ${pe.status === "COMPLETED" ? "pill-ok" : pe.status === "CANCELLED" ? "pill-bad" : "pill-warn"}`}>
                      {pe.status}
                    </span>
                  </td>
                </tr>
              ))}
              {dashboard.postventa_activa.length === 0 && (
                <tr>
                  <td colSpan={4} className="meta">
                    Sin postventa activa en este momento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dashboard?.incidencias && (
        <div className="section-card">
          <h2>Incidencias</h2>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Severidad</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Abierta</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.incidencias.map((i) => (
                <tr key={i.id}>
                  <td>
                    <button className="link-btn" onClick={() => open(i.contact_id)}>
                      Paciente #{i.contact_id}
                    </button>
                  </td>
                  <td>
                    <span className={i.severity === "URGENT" ? "bad" : ""}>{i.severity}</span>
                  </td>
                  <td>{i.issue_type}</td>
                  <td>
                    <span className={i.status === "RESOLVED" ? "ok" : "warn"}>{i.status}</span>
                  </td>
                  <td className="meta">{formatDateTime(i.opened_at)}</td>
                </tr>
              ))}
              {dashboard.incidencias.length === 0 && (
                <tr>
                  <td colSpan={5} className="meta">
                    Sin incidencias abiertas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dashboard?.reventa_recurrencia && (
        <div className="section-card">
          <h2>Reventa / recurrencia</h2>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Oportunidad</th>
                <th>Elegible</th>
                <th>Prioridad</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.reventa_recurrencia.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button className="link-btn" onClick={() => open(r.contact_id)}>
                      Paciente #{r.contact_id}
                    </button>
                  </td>
                  <td>{r.opportunity_type}</td>
                  <td>{formatDateTime(r.eligible_at)}</td>
                  <td>{r.ai_priority !== null && r.ai_priority !== undefined ? Number(r.ai_priority).toFixed(2) : "-"}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
              {dashboard.reventa_recurrencia.length === 0 && (
                <tr>
                  <td colSpan={5} className="meta">
                    Sin oportunidades de reventa elegibles todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dashboard?.ltv_cohortes && (
        <div className="section-card">
          <h2>LTV por paciente</h2>
          <table>
            <thead>
              <tr>
                <th>Paciente</th>
                <th>LTV bruto</th>
                <th>LTV BCM</th>
                <th>Compras</th>
                <th>Última compra</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.ltv_cohortes.map((l) => (
                <tr key={String(l.contact_id)}>
                  <td>
                    <button className="link-btn" onClick={() => open(l.contact_id)}>
                      Paciente #{l.contact_id}
                    </button>
                  </td>
                  <td>{formatCurrency(l.ltv_gross)}</td>
                  <td>{formatCurrency(l.ltv_bcm)}</td>
                  <td>{l.purchase_count}</td>
                  <td className="meta">{formatDateTime(l.last_purchase_at)}</td>
                </tr>
              ))}
              {dashboard.ltv_cohortes.length === 0 && (
                <tr>
                  <td colSpan={5} className="meta">
                    Sin snapshots de LTV todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
